import { Box, IconButton, InputAdornment, Stack, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material'
import { SearchRounded, TuneOutlined } from '@mui/icons-material'
import DownloadIcon from '@mui/icons-material/Download'
import { useEffect, useRef, useState } from 'react'
import AlertUI from '../../generic/AlertUI'
import ImportFile from '../../importFile'
import CircularProgressWithLabel from '../../generic/CircularProgressWithLabel'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../generic/hooks/useDateFormatter'
import exportDetailsTableCsv from './exportDetailsTableCsv'

const TOOLBAR_HEIGHT = 56

const titleStyle = {
  fontSize: '25px !important',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
}

const tabsStyle = {
  minHeight: 40,
  '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontSize: 14 }
}

const countBadgeStyle = {
  px: 0.75,
  borderRadius: 1,
  fontSize: 12,
  lineHeight: '18px',
  bgcolor: theme => theme.palette.action.selected,
  fontVariantNumeric: 'tabular-nums'
}

// When the table scrolls sideways the bar is as wide as the table: these groups stay in view.
const stickyRightStyle = {
  flex: '0 0 auto',
  position: 'sticky',
  right: 24,
  zIndex: 2
}

const TabLabel = ({ label, count }) => (
  <Stack direction='row' spacing={1} alignItems='center'>
    <span>{label}</span>
    {count === undefined ? null : <Box component='span' sx={countBadgeStyle}>{count}</Box>}
  </Stack>
)

/**
 * The general search as a magnifier that opens into a field when pressed. It closes again when it
 * loses the focus empty; with text in it, it stays open so the search in use is always visible.
 */
const ExpandableSearch = ({ onSearchChange }) => {
  const { t_ } = useSmartTranslation()
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const label = t_('Search')

  if (!open) {
    return (
      <Tooltip title={label}>
        <IconButton aria-label={label} onClick={() => setOpen(true)}>
          <SearchRounded />
        </IconButton>
      </Tooltip>
    )
  }
  return (
    <TextField
      size='small'
      autoFocus
      value={text}
      placeholder={t_('Search...')}
      onChange={(event) => {
        setText(event.target.value)
        onSearchChange(event.target.value)
      }}
      onBlur={() => {
        if (text === '') setOpen(false)
      }}
      sx={{ width: 240 }}
      inputProps={{ 'aria-label': label }}
      InputProps={{
        startAdornment: (
          <InputAdornment position='start'>
            <SearchRounded fontSize='small' />
          </InputAdornment>
        )
      }}
    />
  )
}

/**
 * The bar above the planner details table, in one line: the tabs (or the title) on the left; on the
 * right the host's own content, the general search, TUNE and export (as icons) and the import.
 *
 * @param {Object} props
 * @param {String|Object} props.title Text, or a node, shown where the tabs go when there are none
 * @param {Object} props.tabs `{ items: [{ value, label, count }], value, onChange(value) }`
 * @param {Object} props.toolbar `{ render: { fetchFunction }, renderSticky: { fetchFunction } }`, host content
 * @param {Array<Object>} props.columns Declared columns (for import and export)
 * @param {Array<Object>} props.importFile Import bean params, as today
 * @param {Boolean} props.tuneEnabled Shows the TUNE button
 * @param {Function} props.onTuneClick
 * @param {Function} props.onSearchChange Called with the new general search text
 * @param {Function} props.dataFetch
 * @param {Object} props.requestPagination The query the table is showing, for the export
 */
const PlannerDetailsTableToolbar = ({ title, tabs, toolbar, columns, importFile, tuneEnabled, onTuneClick, onSearchChange, dataFetch, requestPagination }) => {
  const { t_ } = useSmartTranslation()
  const { formatDateTime } = useDateFormatter()
  const [AlertElement, showAlert] = AlertUI()
  const [downloadingFile, setDownloadingFile] = useState(false)
  const [progress, setProgress] = useState(0)
  const downloadingResetTimeoutRef = useRef()

  useEffect(() => {
    return () => {
      clearTimeout(downloadingResetTimeoutRef.current)
    }
  }, [])

  const handleDownload = async () => {
    try {
      setProgress(0)
      setDownloadingFile(true)
      await exportDetailsTableCsv({ dataFetch, requestPagination, columns, title, formatDateTime, onProgress: setProgress })
    } catch (error) {
      console.error('Error downloading CSV:', error)
    }
    setProgress(100)
    clearTimeout(downloadingResetTimeoutRef.current)
    downloadingResetTimeoutRef.current = setTimeout(() => setDownloadingFile(false), 600)
  }

  const exportLabel = t_('Export')
  const tuneLabel = t_('TUNE')

  return (
    <>
      {AlertElement}
      <Stack direction='row' alignItems='center' gap={2} sx={{ height: TOOLBAR_HEIGHT, px: 2 }}>
        <Box sx={{ flex: '1 1 auto', minWidth: 0, position: 'sticky', left: 24 }}>
          {tabs
            ? (
              <Tabs value={tabs.value} onChange={(event, value) => tabs.onChange(value)} variant='scrollable' scrollButtons='auto' allowScrollButtonsMobile sx={tabsStyle}>
                {tabs.items.map((item) => <Tab key={item.value} value={item.value} label={<TabLabel label={item.label} count={item.count} />} />)}
              </Tabs>
              )
            : typeof title === 'string'
              ? <Typography title={title} sx={titleStyle}>{title}</Typography>
              : title}
        </Box>
        <Stack direction='row' alignItems='center' spacing={1} sx={stickyRightStyle}>
          {toolbar?.render?.fetchFunction ? toolbar.render.fetchFunction({ showAlert }) : null}
          <ExpandableSearch onSearchChange={onSearchChange} />
          {tuneEnabled && (
            <Tooltip title={tuneLabel}>
              <IconButton aria-label={tuneLabel} onClick={onTuneClick}>
                <TuneOutlined />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={exportLabel}>
            <span>
              <IconButton aria-label={exportLabel} onClick={handleDownload} disabled={downloadingFile}>
                {downloadingFile ? <CircularProgressWithLabel value={progress} /> : <DownloadIcon />}
              </IconButton>
            </span>
          </Tooltip>
          {importFile && <ImportFile beanParams={importFile} columns={columns} />}
          {toolbar?.renderSticky?.fetchFunction ? toolbar.renderSticky.fetchFunction({ showAlert }) : null}
        </Stack>
      </Stack>
    </>
  )
}

export { TOOLBAR_HEIGHT }
export default PlannerDetailsTableToolbar
